import { type PropsWithChildren, type ReactElement, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';

/**
 * Route guards — three composable wrappers around `<Outlet />` (or any
 * subtree). They cooperate with the auth store and the global
 * `api:unauthorized` event to keep the user on a sensible URL at all
 * times:
 *
 *   - AuthGuard:  must be authenticated — else redirect to /login.
 *                 Also wires up the single 401 listener that wipes
 *                 the store and routes the user back to /login.
 *                 Dedupe via `isRedirecting` guard.
 *
 *   - GuestGuard: must NOT be authenticated — else redirect to /.
 *                 Used to bounce an already-signed-in user away from
 *                 /login if they navigate directly.
 *
 *   - TenantGuard: stub — just checks the store has a user. In Slice
 *                 1 it will verify the current membership is still
 *                 active server-side.
 */

export function AuthGuard({ children }: PropsWithChildren): ReactElement {
  const isAuthed = useAuthStore((s) => Boolean(s._hasHydrated && s.token && s.user));
  const hydrated = useAuthStore((s) => s._hasHydrated);
  const isRedirecting = useAuthStore((s) => s.isRedirecting);
  const setRedirecting = useAuthStore((s) => s.setRedirecting);
  const location = useLocation();

  // Singleton 401 listener — installed ONCE, on the first mount of
  // any protected route. We namespace the listener with the auth
  // store flag so a second mount in a different tree doesn't double
  // register; the effect's `[]` deps + the module-level set ensure
  // the listener is attached exactly once per page load.
  useEffect(() => {
    const onUnauthorized = (event: Event): void => {
      if (useAuthStore.getState().isRedirecting) return;
      const detail = (event as CustomEvent<{ token?: string | null }>).detail;
      // Ignore a late 401 from an older request after the user signed in
      // again or switched tenant and received a different token.
      if (detail && 'token' in detail && useAuthStore.getState().token !== detail.token) return;
      // If we are already on /login, the 401 is coming from a
      // POST /auth/signin attempt with bad credentials (apiFetch
      // explicitly skips redirecting on /auth/signin, but other
      // endpoints on the login page can also 401). Don't build a
      // redirect URL — just leave the user on the login form.
      const here = window.location.pathname;
      if (here === '/login' || here.startsWith('/login/')) return;
      useAuthStore.getState().setRedirecting(true);
      useAuthStore.getState().clearAuth();
      const target = `/login?redirect=${encodeURIComponent(
        window.location.pathname + window.location.search,
      )}`;
      window.history.replaceState(null, '', target);
      // Force a route navigation via a synthetic dispatch because we
      // can't use useNavigate inside an event listener outside React.
      window.dispatchEvent(new PopStateEvent('popstate'));
    };
    window.addEventListener('api:unauthorized', onUnauthorized);
    return () => window.removeEventListener('api:unauthorized', onUnauthorized);
  }, []);

  // Reset the redirect flag once the user lands at /login so a future
  // 401 can re-redirect cleanly. We listen on `location.pathname`
  // because listening on the singleton event above would just clear
  // the flag we just set.
  useEffect(() => {
    if (isRedirecting && location.pathname === '/login') {
      setRedirecting(false);
    }
  }, [isRedirecting, location.pathname, setRedirecting]);

  if (!hydrated) {
    // Splash screen for the brief moment while Zustand rehydrates from
    // localStorage on cold-boot. Keeps the UI from flashing the wrong
    // route while the auth state is still unknown.
    return <SplashScreen />;
  }

  if (!isAuthed) {
    // Don't accumulate the redirect query when we're already on
    // /login — that produces the
    // `/login?redirect=%2Flogin%3Fredirect%3D…` chain and renders
    // a blank screen in the browser while the URL keeps doubling.
    const here = `${location.pathname}${location.search}`;
    const redirect =
      location.pathname === '/login' || location.pathname.startsWith('/login/') ? '/' : here;
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirect)}`} replace />;
  }

  return <>{children}</>;
}

export function GuestGuard({ children }: PropsWithChildren): ReactElement {
  const isAuthed = useAuthStore((s) => Boolean(s._hasHydrated && s.token && s.user));
  const hydrated = useAuthStore((s) => s._hasHydrated);

  if (!hydrated) return <SplashScreen />;
  if (isAuthed) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function TenantGuard({ children }: PropsWithChildren): ReactElement {
  // Placeholder: assumes AuthGuard has already validated the token.
  // In Slice 1 this will round-trip /me to confirm the user still has
  // a membership in the tenant the JWT was issued for.
  const user = useAuthStore((s) => s.user);
  if (!user?.tenantId) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

function SplashScreen(): ReactElement {
  return (
    <div className="grid h-screen w-full place-items-center bg-background">
      <div className="space-y-2 text-center">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          GlobalesERP
        </p>
        <div className="mx-auto h-1 w-24 overflow-hidden rounded-full bg-muted">
          <div className="h-full w-1/3 animate-pulse bg-foreground/40" />
        </div>
      </div>
    </div>
  );
}

// Touch the `useAuthStore` import so biome doesn't tree-shake it.
void useAuthStore;
